<template>
  <div>
    <app-page-title title="Member Management" :color="'lips'">
      <template #pageTitleLeft>
        <h1>Member Management</h1>
      </template>
      <template #pageTitleRight>
        <div class="_text-right">
          <router-link
            v-if="$store.state.auth.minimumAccess.memberMaintenanceAllowed"
            :to="{ name: 'add-new-family', params: { clear: true } }"
            class="btn --sm"
          >
            Add Subscriber
          </router-link>
        </div>
      </template>
    </app-page-title>

    <cloud-cannon-content
      class="_p0 _ptxs bg-light-gray"
      :content-loading="cloudCannonContentLoading"
      :content="cloudCannonContent.header"
    />

    <app-advanced-table-section
      class="_bt1"
      v-if="!isInternalUser"
      id="draft-members"
      :custom-sort="tableSorter"
      title="Pending Member Transactions"
      :loading='pendingTransactionsLoading'
      :headers="headers.draftMembersHeaders"
      :items="permissionedDraftMembers"
      @cellClicked="transactionClicked"
    >
      <template #actions>
        <app-column-picker :headers='headers.draftMembersHeaders' @updateHeaders="updateHeaders('draftMembersHeaders', $event)" />
      </template>
    </app-advanced-table-section>

    <app-advanced-table-section
      class="_bt1"
      :custom-sort="tableSorter"
      title="Recently Added Members"
      id="recently-added"
      :loading='recentlyAddedLoading'
      :headers="headers.recentlyAddedMembersHeaders"
      :items="recentlyAddedMembers"
      :noDataText="recentlyAddedUserMessage"
      @cellClicked="memberClicked"
    >
      <template #actions>
        <app-column-picker :headers="headers.recentlyAddedMembersHeaders" @updateHeaders="updateHeaders('recentlyAddedMembersHeaders', $event)" />
      </template>
    </app-advanced-table-section>

    <app-advanced-table-section
      class="_bt1"
      :custom-sort="tableSorter"
      title="Recently Updated Members"
      id="recently-updated"
      :headers="headers.recentlyUpdatedMembersHeaders"
      :loading='recentlyUpdatedLoading'
      :items="recentlyUpdatedMembers"
      :noDataText="recentlyUpdatedUserMessage"
      @cellClicked="memberClicked"
    >
      <template #actions>
        <app-column-picker :headers="headers.recentlyUpdatedMembersHeaders" @updateHeaders="updateHeaders('recentlyUpdatedMembersHeaders', $event)" />
      </template>
    </app-advanced-table-section>

    <cloud-cannon-content
      class="_p0 _ptxs _pbxs bg-light-gray"
      :content-loading="cloudCannonContentLoading"
      :content="cloudCannonContent.footer"
    />
  </div>
</template>

<script>
  import AppAdvancedTableSection from '@sections/AdvancedTableSection.vue';
  import AppColumnPicker from '@molecules/ColumnPicker.vue';
  import AppPageTitle from '@sections/PageTitle';
  import tableSorter from '@mixins/tableSorter';
  import moment from 'moment';
  import MemberApi from '@api/MemberApi';
  import { mapGetters } from 'vuex';
  import fetchable from '@mixins/fetchable';
  import CloudCannonable from '@mixins/CloudCannonable';
  import { computed, onBeforeMount, reactive, ref, watch } from '@vue/composition-api';
  import { useRouter, useStore } from '@vue3shims';
  import { formatDate, toIso8601Date, fixPagrParams } from '@utils';

  export default {
    mixins: [
      tableSorter,
      fetchable,
      CloudCannonable
    ],
    components: {
      AppAdvancedTableSection,
      AppColumnPicker,
      AppPageTitle
    },
    setup() {
      // basics
      const router = useRouter();
      const store = useStore();

      // const
      const fetchers = [
        {
          action: 'cloudCannon/GET_CLOUD_CANNON_CONTENT',
          options: {
            cloudCannonResource: '/member-management.json'
          },
          config: {
            root: true
          }
        }
      ];

      // reactives
      const draftMembers = ref([]);
      const err = ref(false);
      const headers = reactive({
        draftMembersHeaders:[
          { text: 'Last Name', align: 'left', value: 'memberLastName' },
          { text: 'First Name', value: 'memberFirstName' },
          { text: 'EyeMed Member ID', value: 'eyemedMemberId', visible: false },
          { text: 'Client\'s Member ID', value: 'clientHealthId' },
          { text: 'Group', value: 'group' },
          { text: 'Subgroup', value: 'subGroup', visible: false },
          { text: 'Relationship', value: 'relationship' },
          { text: 'DOB', value: 'dob' },
          { text: 'Last 4 SSN', value: 'ssn', visible: false },
          { text: 'Submitted On', value: 'createdDate', visible: false },
          { text: 'Submitted By', value: 'createdBy', visible: false }
        ],
        recentlyAddedMembersHeaders: [
          { text: 'Last Name', align: 'left', value: 'memberLastName' },
          { text: 'First Name', value: 'memberFirstName' },
          { text: 'EyeMed Member ID', value: 'eyemedMemberId', visible: false },
          { text: 'Client\'s Member ID', value: 'clientHealthId' },
          { text: 'Group', value: 'group' },
          { text: 'Subgroup', value: 'subGroup', visible: false },
          { text: 'Relationship', value: 'relationship' },
          { text: 'DOB', value: 'dob' },
          { text: 'Last 4 SSN', value: 'ssn', visible: false },
          { text: 'Submitted On', value: 'createdDate', visible: false },
          { text: 'Submitted By', value: 'createdBy', visible: false }
        ],
        recentlyUpdatedMembersHeaders: [
          { text: 'Last Name', align: 'left', value: 'memberLastName' },
          { text: 'First Name', value: 'memberFirstName' },
          { text: 'EyeMed Member ID', value: 'eyemedMemberId', visible: false },
          { text: 'Client\'s Member ID', value: 'clientHealthId' },
          { text: 'Group', value: 'group' },
          { text: 'Subgroup', value: 'subGroup', visible: false },
          { text: 'Relationship', value: 'relationship' },
          { text: 'DOB', value: 'dob' },
          { text: 'Last 4 SSN', value: 'ssn', visible: false },
          { text: 'Submitted On', value: 'createdDate', visible: false },
          { text: 'Submitted By', value: 'createdBy', visible: false }
        ],
      });
      const loaded = ref(false);
      const pendingTransactionsLoading = ref(false);
      const recentlyAddedLoading = ref (true);
      const recentlyAddedMembers = ref([]);
      const recentlyAddedUserMessage = ref('');
      const recentlyUpdatedLoading = ref(true);
      const recentlyUpdatedMembers = ref([]);
      const recentlyUpdatedUserMessage = ref('');
      
      // computed
      const groupSelection = computed(() => store.getters['groupSelector/selected']);
      const isInternalUser = computed(() => store.getters['isInternalUser'] || false);
      const permissionedDraftMembers = computed(() => {
        let subGroupCks = subGroupAccess.value.map(sg => Number(sg.subGroupCk));
        return draftMembers.value.filter(draftMember => subGroupCks.includes(Number(draftMember.coverage.subGroupCk)));
      });
      const subGroupAccess = computed(() => store.getters['subGroupAccess'] || []);
      const transactionsBody = computed(() => {
        return {
          fromDate: toIso8601Date(moment().subtract(7, 'd')),
          toDate: toIso8601Date(moment()),
          userId: store.getters['userId'],
          ...groupSelection.value,
        }
      });

      // methods
      const fetchData = () => {
        recentlyAddedLoading.value = true;
        recentlyUpdatedLoading.value = true;
        if (!isInternalUser.value) {
          pendingTransactionsLoading.value = true;
          new MemberApi({ simulateExample: 'Draft Trans - Subscriber' }).requestDrafts()
            .then(res => {
              if(res.status && res.status === 'memberTransactionNotFound') {
                draftMembers.value = [];
              }
              else {
                draftMembers.value = transformMemberResults(Array.isArray(res) ? res : [res]);
              }
              // console.log(`draftMembers`, draftMembers.value);
            })
            .catch(err => {
              // console.log(`draft error`);
              console.error(err)
            })
            .finally(() => {
              pendingTransactionsLoading.value = false;
              // console.log(`draft finally`, pendingTransactionsLoading.value);
            })
        }

        let fetchOpts = fixPagrParams(transactionsBody.value)
        fetchOpts.userId = ''
        fetchOpts.fromDate = transactionsBody.value.fromDate
        fetchOpts.toDate = transactionsBody.value.toDate

        new MemberApi({
          simulateExample: 'Transaction History - External User - ADD'
        })
          .transactionHistory({ body: { transactionType: 'ADD', ...fetchOpts }})
          .then(res => {
            recentlyAddedMembers.value = !res ? [] : transformMemberResults(res);
            if (res && !Array.isArray(res)) { recentlyAddedUserMessage.value = res.userMessage }
            // console.log(`recentlyAdded`, recentlyAddedMembers.value);
          })
          .catch(err => {
            // TODO: actually handle this.
            // console.log(`recent errorz`);
            console.error(err);
          })
          .finally(() => { 
            recentlyAddedLoading.value = false;
            // console.log(`recent finally`, recentlyAddedLoading.value);
          });

        new MemberApi({
          simulateExample: 'Transaction History-External User - With Userid'
        })
          .transactionHistory({ body: { transactionType: 'UPDATE', ...fetchOpts }})
          .then(res => {
            recentlyUpdatedMembers.value = !res ? [] : transformMemberResults(res)
            if (res && !Array.isArray(res)) { recentlyUpdatedUserMessage.value = res.userMessage }
            // console.log(`recentlyUpdated`, recentlyUpdatedMembers.value);
          })
          .catch(err => {
            // console.log(`recentlyUpdated error`);
            console.error(err)
          })
          .finally(() => {
            recentlyUpdatedLoading.value = false;
            // console.log(`recentlyUpdated finally`, recentlyUpdatedLoading.value);
          });
      }
      const memberClicked = (member) => {
        router.push({
          name: 'member-details',
          params: {
            memberCk: member.memberCk
          }
        })
      }
      const transactionClicked  = (transaction) => {
        switch (transaction.action) {
          case 'ADD_SUBSCRIBER': {
            router.push({
              name: 'primary-subscriber-details',
              query: {
                transactionId: transaction.transactionId
              }
            });
            break;
          }
          case 'ADD_DEPENDENT': {
            router.push({
              name: 'add-dependent',
              query: {
                transactionId: transaction.transactionId
              }
            });
            break;
          }
          default: break;
        }
      }
      const updateHeaders = (headerName, updatedHeaders) => {
        headers[headerName] = updatedHeaders;
      }
      const transformMemberResults = (members = []) => {
        if (!Array.isArray(members)) { return [] } else {
          try {
            return members.map(m => {
              // console.log(`m`, m);
              return {
                ...m,
                ...{
                  group: `${m.coverage.groupName} - ${m.coverage.groupId}`,
                  subGroup: `${m.coverage.subGroupName} - ${m.coverage.subGroupId}`,
                  dob: formatDate(m.memberDateOfBirth),
                  createdDate: formatDate(m.createdDate)
                }
              }
            }
            )
          } catch (error) {
            console.error(error)
            return [];
          }
        }
      }

      onBeforeMount(() => {
        fetchData();
      });

      watch(groupSelection, () => {
        fetchData();
      })

      return {
        draftMembers,
        err,
        fetchData,
        fetchers, 
        headers,
        isInternalUser,
        loaded,
        memberClicked,
        pendingTransactionsLoading,
        permissionedDraftMembers,
        recentlyAddedLoading,
        recentlyAddedMembers,
        recentlyAddedUserMessage,
        recentlyUpdatedLoading,
        recentlyUpdatedMembers,
        recentlyUpdatedUserMessage,
        subGroupAccess,
        transactionClicked,
        transactionsBody,
        transformMemberResults,
        updateHeaders,
      }
    },
  }
</script>
